import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/feature/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8b69k6oi3g3tv8ukp0";

const highlights = [
  { icon: "ri-movie-2-line", value: "500+", label: "影视作品收录" },
  { icon: "ri-newspaper-line", value: "每日", label: "娱乐资讯更新" },
  { icon: "ri-layout-grid-line", value: "3", label: "核心内容频道" },
  { icon: "ri-user-heart-line", value: "0", label: "观看门槛" },
];

const values = [
  {
    icon: "ri-focus-3-line",
    title: "内容聚焦",
    desc: "专注电影、电视剧、综艺三大内容方向，把每一类都做深做透，让用户快速找到想看的内容。",
  },
  {
    icon: "ri-timer-flash-line",
    title: "资讯时效",
    desc: "紧跟影视圈动态，第一时间呈现作品口碑、幕后故事与行业趋势，帮助用户掌握娱乐脉搏。",
  },
  {
    icon: "ri-shield-check-line",
    title: "清晰界面",
    desc: "坚持简洁克制的内容排版，用清晰的层级与充足的留白，让浏览与阅读真正舒适。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = "关于爱妻艺 - 综合影视作品与娱乐资讯平台";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "了解爱妻艺的品牌定位、内容理念与发展愿景，获取联系方式并留言给我们。",
      );
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢您的留言，我们会尽快与您联系。");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : value.name);
    });

    setStatus("submitting");
    setMessage("正在提交，请稍候…");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("留言提交成功，感谢您的来信，我们会尽快回复。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于爱妻艺" }]} />

      <section className="mt-4 overflow-hidden rounded-lg bg-gradient-to-r from-primary-700 to-primary-500 p-6 md:p-10">
        <h1 className="text-2xl font-bold text-background-50 md:text-4xl">关于爱妻艺</h1>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-background-100 md:text-base md:leading-8">
          爱妻艺是一个综合性的影视作品展示与娱乐资讯发布平台，涵盖电影、电视剧、综艺等多类别内容。
          我们致力于为用户提供专业丰富、界面清晰的多内容服务，突出影视资源的多样性与资讯的时效性，
          让每一位喜爱影视剧和综艺节目的用户，都能在这里轻松发现好作品。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/movie"
            className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            浏览影视作品
          </Link>
          <Link
            to="/news"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
          >
            阅读最新资讯
          </Link>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {highlights.map((item) => (
          <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className={`${item.icon} text-xl`}></i>
            </span>
            <p className="mt-3 text-xl font-bold text-foreground-950 md:text-2xl">{item.value}</p>
            <p className="mt-1 text-xs text-foreground-500 md:text-sm">{item.label}</p>
          </div>
        ))}
      </section>

      <section className="mt-8">
        <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
          <span className="h-5 w-1 rounded-full bg-accent-500"></span>
          品牌定位与发展愿景
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-3.5 text-base font-semibold text-foreground-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-lg bg-background-100 p-5 md:p-6">
          <h3 className="text-base font-semibold text-foreground-900">我们的愿景</h3>
          <p className="mt-2 text-sm leading-7 text-foreground-600">
            我们希望通过持续的内容沉淀与体验打磨，让爱妻艺成为用户心中可靠的娱乐内容入口。
            无论你是想找一部周末片单，还是想了解最新的影视动态，都能在这里获得清晰、及时、有温度的答案。
          </p>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            联系我们
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            无论你是内容合作、意见反馈，还是想推荐一部好作品，都欢迎通过右侧留言表单与我们联系。
          </p>
          <ul className="mt-5 flex flex-col gap-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-mail-line"></i>
              </span>
              <span className="text-foreground-700">合作邮箱：contact@aiqiyi-media.example</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-map-pin-line"></i>
              </span>
              <span className="text-foreground-700">内容中心：北京市朝阳区创意产业园 A 座</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-time-line"></i>
              </span>
              <span className="text-foreground-700">工作时间：周一至周五 10:00 - 19:00</span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="text-base font-bold text-foreground-950">留言给我们</h2>
          <form
            id="about-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-4 flex flex-col gap-3"
          >
            <div>
              <label htmlFor="contact-name" className="mb-1 block text-xs text-foreground-500">
                您的称呼
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="请输入您的称呼"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="mb-1 block text-xs text-foreground-500">
                联系邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="请输入您的邮箱"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>

            <div>
              <label htmlFor="contact-subject" className="mb-1 block text-xs text-foreground-500">
                留言类型
              </label>
              <select
                id="contact-subject"
                name="subject"
                className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              >
                <option value="内容合作">内容合作</option>
                <option value="意见反馈">意见反馈</option>
                <option value="作品推荐">作品推荐</option>
                <option value="其他咨询">其他咨询</option>
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="mb-1 block text-xs text-foreground-500">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={4}
                required
                maxLength={500}
                placeholder="请输入您的留言（不超过 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>

            <input
              type="text"
              name="website_alt"
              className="form-verify-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-1 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-send-plane-line"></i>
              {status === "submitting" ? "提交中…" : "提交留言"}
            </button>

            {status !== "idle" && (
              <p
                className={`rounded-md px-3 py-2 text-sm ${
                  status === "success"
                    ? "bg-primary-50 text-primary-700"
                    : status === "error"
                      ? "bg-accent-50 text-accent-700"
                      : "bg-background-100 text-foreground-600"
                }`}
              >
                {message}
              </p>
            )}
          </form>
        </div>
      </section>

      <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <i className="ri-copyright-line text-primary-600"></i>
          版权声明与免责提示
        </h2>
        <div className="mt-3 flex flex-col gap-2 text-sm leading-7 text-foreground-600">
          <p>
            本站展示的影视作品信息与娱乐资讯均为演示用途，作品名称、人物、剧情等信息为示例内容，
            不代表任何真实作品或事件。
          </p>
          <p>
            爱妻艺不提供任何影视播放、下载或资源分享功能，站内所有剧集目录仅用于文字简介浏览演示。
          </p>
          <p>
            如站内内容涉及版权问题，请通过上方联系方式与我们取得联系，我们会第一时间核实并处理。
          </p>
          <p>© 2025 爱妻艺 AIQIYI MEDIA. 保留所有权利。</p>
        </div>
      </section>
    </div>
  );
}